import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { LabelType, Options } from '@angular-slider/ngx-slider';
import { MatSnackBar } from '@angular/material/snack-bar';

import { OpenMold, Gravity } from '@usimweb/core/models';
import { C3Service, ModelService, ViewerComponent, Vec3, Color3 } from '@usimweb/ceetron';
import { AuthHandler } from '@usimweb/core-auth';
import { ApiService } from '@usimweb/core/services';
import { InfoDialogComponent } from '@usimweb/ui/dialogs';
import { DialogService } from '@usimweb/ui/dialogs';

import { map, skip } from 'rxjs/operators';
import { InputType } from '../../../../../core/enums/inpuTypeEnum';

import { PointSelectionService } from '../../../../../core/services/point-selection/point-selection.service';
import { UIStore } from '@usimweb/state/store';
import { OpenMoldDialogComponent } from 'apps/foaming/src/app/dialogs/open-mold-dialog/open-mold-dialog.component';
import { MoldClosingDialogComponent } from 'apps/foaming/src/app/dialogs/mold-closing-dialog/mold-closing-dialog.component';

@Component({
    selector: 'app-open-mold',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './open-mold.component.html',
    styleUrls: ['./open-mold.component.scss'],
    standalone: false
})
export class OpenMoldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() injectionTime: number = 0;

  @Input() injectionRate: number = 0;

  @Input() density: number = 0;

  @Input() modelId: number = 0;

  @Input() gravity: Gravity = { x: 0, y: 0, z: 0 };

  @Output() selectionChanged: EventEmitter<any> = new EventEmitter<any>();

  @Output() moldClosingChanged: EventEmitter<{ id: number }> = new EventEmitter<{ id: number }>();

  public viewer = 0;

  public value: number = 1;

  public pointAmount = 0;

  public nozzleOffset: number = this.pointSelectionService.getPointOffset();

  public isOffsetClicked: boolean = false;

  public loadViewer = false;

  public options: Options = {
    showTicks: false,
    showTicksValues: true,
    stepsArray: [
      { value: 0, legend: 'Coarse' },
      { value: 5, legend: 'Medium' },
      { value: 10, legend: 'Fine' },
    ],

    translate: (value: number, label: LabelType): string => {
      switch (value) {
        case 5:
          return 'Particle resolution';
        default:
          return '';
      }
    },
    ticksTooltip: (v: number): string => {
      return 'Sets the particle fineness...';
    },
  };

  public closing_time_offset: number = 1;
  //public closing_time_duration_offset: number = 5;
  public isParticle: boolean = false;
  public showSlider: boolean = false;
  public isClosingSetup: boolean = false;

  public openMold: OpenMold = {
    start: 0,
    duration: 0,
    moldClosingTimeDuration: 5,
    moldClosingTime: 1,
    nozzleDiameter: undefined,
    nozzleVelocity: 14,
    injectionRate: 0,
    injectionTime: 0,
    density: 0,
    sph: 5,
    isClosingSetup: false,
  };

  inputType = InputType;

  @ViewChild('viewer') viewerComponent!: ViewerComponent;

  constructor(
    private apiService: ApiService,
    private authHandler: AuthHandler,
    private dialogService: DialogService,
    private pointSelectionService: PointSelectionService,
    private cd: ChangeDetectorRef,
    private modelService: ModelService,
    private c3Service: C3Service,
    private snackBar: MatSnackBar,
    private uiStore: UIStore
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.injectionTime && changes['injectionTime']) {
      this.openMold.injectionTime = changes['injectionTime'].currentValue;
      this.openMold.start = this.openMold.injectionTime;
      this.openMold.moldClosingTime = this.openMold.start + this.closing_time_offset;
    }

    if (this.injectionRate && changes['injectionRate']) {
      this.openMold.injectionRate = changes['injectionRate'].currentValue;
    }

    if (this.density && changes['density']) {
      this.openMold.density = changes['density'].currentValue;
    }
  }

  public nozzleDiameterChanged(value: string): void {
    this.openMold.nozzleDiameter = parseFloat(value);
    this.openMold.nozzleVelocity =
      Math.round(
        (this.openMold.injectionRate /
          this.openMold.density /
          (((this.openMold.nozzleDiameter / 1000) * 2 * Math.PI) / 4)) *
          1e3
      ) / 1e3;
  }

  public startValueChanged(value: string): void {
    this.openMold.start = parseFloat(value);
    this.openMold.moldClosingTime = this.openMold.start + this.closing_time_offset;

    this.openMold.moldClosingTimeDuration = this.openMold.duration + this.openMold.moldClosingTime;
  }

  public durationValueChanged(value: string): void {
    this.openMold.duration = parseFloat(value);
    this.openMold.moldClosingTimeDuration = this.openMold.duration + this.openMold.moldClosingTime;
  }

  public calcMoldClosingTime(value: string): void {
    this.closing_time_offset = parseFloat(value);
    this.openMold.moldClosingTime = this.closing_time_offset + this.openMold.start;

    this.openMold.moldClosingTimeDuration = this.openMold.duration + this.openMold.moldClosingTime;

    this.blur();
  }

  public blur() {
    this.openMold.offset = this.pointSelectionService.getPointOffset();
    this.openMold.path = this.pointSelectionService.getInfoPoints();
    this.selectionChanged.emit(this.openMold);
  }

  public openPointSelectionInfo() {
    this.dialogService.openContentDialog(
      OpenMoldDialogComponent,
      {
        injectionTime: this.injectionTime,
        modelId: this.viewer,
        gravity: this.gravity,
      },
      'Add data to selected point',
      'Save',
      85
    );
  }

  public toggleVector(isChecked: boolean) {
    this.pointSelectionService.toggleVectors(this.viewer, isChecked);
  }

  public nozzleOffsetChanged(value: string) {
    if (!isNaN(parseFloat(value))) {
      this.pointSelectionService.setPointOffset(parseFloat(value), this.viewer);
      this.nozzleOffset = parseFloat(value) || 0.1;
      this.isOffsetClicked = false;
    } else {
      this.snackBar.open('Please insert a valid number');
    }
  }

  public deleteAllPoints() {
    this.pointSelectionService.clearPoints(this.viewer);
    this.uiStore.clear('this.STORE_KEY' + this.viewer.toString());
  }

  public refinementLevelChanged(refinementLevel: number) {
    this.openMold.sph = refinementLevel || 0;
    this.selectionChanged.emit(this.openMold);
  }

  public toggleParticleMode(isParticleMode: boolean) {
    if (isParticleMode) {
      this.dialogService
        .openTextDialog(
          InfoDialogComponent,
          {},
          'Disclaimer',
          'OK',
          'Notice: If you enable this option, you data will be send to a third party API.'
        )
        .subscribe((data) => {
          if (data) {
            this.showSlider = true;
            this.cd.detectChanges();
          } else {
            this.showSlider = false;
            this.isParticle = false;
            this.cd.detectChanges();
          }
        });
    } else {
      this.showSlider = false;
      this.cd.detectChanges();
    }
  }

  public openClosingMoldDialog(isClosingSetup: boolean) {
    if (isClosingSetup) {
      let vec3: cee.Vec3 = new Vec3(this.gravity.x, this.gravity.y, this.gravity.z);
      vec3 = Vec3.negate(vec3);

      const strGravity: string = vec3.x + ', ' + vec3.y + ', ' + vec3.z;
      const data = { gravity: strGravity, modelId: this.modelId };
      this.dialogService
        .openContentDialog(MoldClosingDialogComponent, data, 'Mold closing setup', 'Save', 20)
        .subscribe((data) => {
          if (!data) {
            this.openMold.isClosingSetup = false;
            this.isClosingSetup = false;
            this.cd.detectChanges();
            return;
          }
          this.openMold.isClosingSetup = true;
          this.moldClosingChanged.emit(data);
          this.cd.detectChanges();
        });
    }
  }

  public previewLoadModel(uuid: string) {
    this.loadViewer = true;
    this.cd.detectChanges();

    let width = window.screen.width * 0.35;

    if (screen.width < 1200) {
      width = window.screen.width * 0.7;
    }

    this.c3Service._viewer['0'].resizeViewer(width, window.screen.height * 0.42);
    this.c3Service._view['0'].backgroundColor = new Color3(246, 246, 246);

    const access_token = this.authHandler.accessToken || '';

    const subscriptionToken = this.apiService.getMeshingVersionToken(uuid, access_token).subscribe((token) => {
      subscriptionToken.unsubscribe();

      this.modelService.clearTask();
      this.modelService.openModel(token as any);
      this.pointSelectionService.initSelectionListener(this.viewer);

      this.uiStore
        .getStateById$('openMold_origin')
        .pipe(
          skip(1),
          map((x) => {
            if (!x) {
              return [];
            }
            return x;
          })
        )
        .subscribe((origin: any[]) => {
          this.pointAmount = origin.length;
          this.cd.detectChanges();
        });
    });
  }

  public nozzleInput() {
    this.isOffsetClicked = true;
  }

  public openInfoDialog() {
    const text = [];

    text.push('3 steps to a successful open mold simulation');
    text.push('_________________________________________________');
    text.push(' 1. Click + mouse move to rotate the 3D object');
    text.push(' 2. Click + Ctrl to pan the 3D object');
    text.push(' 3. Click into 3D object to draw the nozze path');

    this.dialogService.openToUDialog(InfoDialogComponent, 'Open mold info', text);
  }

  ngOnDestroy() {
    this.modelService.clearTask();
    this.uiStore.clear('this.STORE_KEY' + this.viewer.toString());
  }
}
